/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './political-campaign-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {TeamApiService} from '../admin/team/team-api.service';
import {ElectionApiService} from '../common/controller/election-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {PoliticalCampaignApiService, PoliticalCampaignModel} from './political-campaign-api.service';
import {PoliticalEntityApiService} from '../political-entity/political-entity-api.service';
import {APP_SHARED} from '../common/app-shared';
import {FORM_CONTROLS} from '../common/directives/form-controls/form-controls';

const POLITICAL_ENTITY_QUERY_PAGE_SIZE = 2000;

function isPresent(value: any): boolean {
  return value !== null && value !== undefined;
}

function unwrapDomainRef(value: any): any {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'id')) {
    return value.id;
  }
  return value;
}

function stashOwningEntityForCrumbs(m: any): void {
  const raw = isPresent(m.politicalEntity) ? m.politicalEntity : m.politicalEntityId;
  if (raw && typeof raw === 'object' && raw.id) {
    const parentRaw = isPresent(raw.parent) ? raw.parent : raw.parentId;
    let parentStash: any = null;
    if (parentRaw && typeof parentRaw === 'object' && parentRaw.id) {
      parentStash = {id: parentRaw.id, name: parentRaw.name};
    } else if (isPresent(parentRaw) && parentRaw !== '') {
      parentStash = {id: unwrapDomainRef(parentRaw), name: ''};
    }
    m._politicalCampaignCrumbOwning = {
      id: raw.id,
      name: raw.name,
      type: raw.type,
      parent: parentStash
    };
  }
}

function normaliseCampaignRefs(m: any): void {
  stashOwningEntityForCrumbs(m);
  m.politicalEntity = unwrapDomainRef(isPresent(m.politicalEntity) ? m.politicalEntity : m.politicalEntityId);
  m.election = unwrapDomainRef(isPresent(m.election) ? m.election : m.electionId);
  m.payerPoliticalEntity = unwrapDomainRef(isPresent(m.payerPoliticalEntity) ? m.payerPoliticalEntity : m.payerPoliticalEntityId);
  m.ownerTeam = unwrapDomainRef(isPresent(m.ownerTeam) ? m.ownerTeam : m.ownerTeamId);
  delete m.politicalEntityId;
  delete m.electionId;
  delete m.payerPoliticalEntityId;
  delete m.ownerTeamId;
  if (m.topics && m.topics.length) {
    _.forEach(m.topics, (topic: any) => {
      topic.payerPoliticalEntity = unwrapDomainRef(
        isPresent(topic.payerPoliticalEntity) ? topic.payerPoliticalEntity : topic.payerPoliticalEntityId
      );
      delete topic.payerPoliticalEntityId;
    });
  }
}

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, TranslateModule, ...FORM_CONTROLS],
  selector: 'adn-political-campaign-edit-page',
  template
})
export class PoliticalCampaignEditPageComponent extends SignalAdminResourceEditPageBase<PoliticalCampaignModel> {
  private readonly politicalEntityApi = inject(PoliticalEntityApiService);

  availableCurrencies: string[] = [];
  private readonly availableEntitiesState = signal<any[]>([]);
  private readonly owningEntitiesState = signal<any[]>([]);
  private readonly payerEntitiesState = signal<any[]>([]);
  private readonly availableElectionsState = signal<any[]>([]);
  private readonly availableTeamsState = signal<any[]>([]);
  get availableEntities(): any[] { return this.availableEntitiesState(); }
  set availableEntities(value: any[]) { this.availableEntitiesState.set(value); }
  get owningEntities(): any[] { return this.owningEntitiesState(); }
  set owningEntities(value: any[]) { this.owningEntitiesState.set(value); }
  get payerEntities(): any[] { return this.payerEntitiesState(); }
  set payerEntities(value: any[]) { this.payerEntitiesState.set(value); }
  get availableElections(): any[] { return this.availableElectionsState(); }
  set availableElections(value: any[]) { this.availableElectionsState.set(value); }
  get availableTeams(): any[] { return this.availableTeamsState(); }
  set availableTeams(value: any[]) { this.availableTeamsState.set(value); }

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly electionApi: ElectionApiService,
    private readonly teamApi: TeamApiService
  ) {
    super(inject(PoliticalCampaignApiService), 'politicalCampaign');
    this.availableCurrencies = LocalNetworkProfile.get('availableCurrencies') || [];
  }

  protected override initialiseModel(model: PoliticalCampaignModel): PoliticalCampaignModel {
    const initialised = super.initialiseModel(model);
    initialised.topics = initialised.topics || [];
    normaliseCampaignRefs(initialised);
    this.loadEntities();
    this.loadElections();
    this.loadTeams();
    if (!initialised.topics.length) {
      initialised.topics.push({id: '', name: '', amount: {}, payerPoliticalEntity: ''});
    }
    return initialised;
  }

  protected override afterSave(model: PoliticalCampaignModel): void {
    normaliseCampaignRefs(model);
    this.loadEntities();
    this.loadElections();
    this.loadTeams();
  }

  addTopic(): void {
    this.editingModel.topics = this.editingModel.topics || [];
    this.editingModel.topics.push({
      id: '',
      name: '',
      amount: {},
      payerPoliticalEntity: ''
    });
    this.markExternalFormDirty();
  }

  removeTopic(index: number): void {
    this.editingModel.topics.splice(index, 1);
    this.markExternalFormDirty();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackById(_: number, item: any): string {
    return item.id;
  }

  private loadEntities(): void {
    this.politicalEntityApi.listAll(POLITICAL_ENTITY_QUERY_PAGE_SIZE).subscribe({
      next: (page) => {
        this.availableEntities = page.results || [];
        this.owningEntities = this.availableEntities.filter((entity: any) => {
          return entity.type === 'CONTROLLER' || entity.type === 'SPONSOR';
        });
        this.payerEntities = this.availableEntities.filter((entity: any) => {
          return entity.type === 'PAYER';
        });
      },
      error: () => {
        this.availableEntities = [];
        this.owningEntities = [];
        this.payerEntities = [];
      }
    });
  }

  private loadElections(): void {
    this.electionApi.list({pageSize: 200}).subscribe({
      next: (page: any) => {
        this.availableElections = page.results || [];
      },
      error: () => {
        this.availableElections = [];
      }
    });
  }

  private loadTeams(): void {
    this.teamApi.listOptions({minimal: true, pageSize: 2000}).subscribe((page) => {
      this.availableTeams = (page.results || []).filter((team: any) => {
        return team.type === 'CONTROLLER' || team.type === 'SPONSOR';
      });
    }, () => {
      this.availableTeams = [];
    });
  }
}
